import { checkPermissions, useAuth } from "@elit-hair/elit-tools";
import { Button, InitialsAvatar, useTheme } from "@elit-hair/elit-ui";
import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    TbBuildingHospital,
    TbCalendar,
    TbChevronRight,
    TbClockHour4,
    TbLogout,
    TbMoon,
    TbSettings,
    TbSun,
    TbUsers,
} from "react-icons/tb";

import { APP_ROUTES } from "@/components/router/routes";
import { PERMISSIONS } from "@/constants/permissions";
import { cn } from "@/lib/utils";

export type MobileMenuProps = {
    open: boolean;
    onClose: () => void;
};

type MobileRoute = {
    path: string;
    title: string;
    icon: typeof TbCalendar;
    permissions?: string[];
};

const mobileRoutes: MobileRoute[] = [
    {
        path: APP_ROUTES.ROOT,
        title: "sessions",
        icon: TbCalendar,
        permissions: [PERMISSIONS.PRP_SESSIONS_LIST],
    },
    {
        path: APP_ROUTES.CLINICS,
        title: "clinics",
        icon: TbBuildingHospital,
        permissions: [PERMISSIONS.TREATMENT_CLINICS_LIST],
    },
    {
        path: APP_ROUTES.STAFF,
        title: "staff",
        icon: TbUsers,
        permissions: [PERMISSIONS.TREATMENT_STAFF_LIST],
    },
    {
        path: APP_ROUTES.SHIFTS,
        title: "shifts",
        icon: TbClockHour4,
        permissions: [PERMISSIONS.TREATMENT_SHIFTS_CALENDAR],
    },
];

export function MobileMenu({ open, onClose }: MobileMenuProps) {
    const { t } = useTranslation();
    const routerState = useRouterState();
    const pathname = routerState.location.pathname;
    const { userData, handleLogout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const drawerRef = useRef<HTMLDivElement>(null);
    const [show, setShow] = useState(false);
    const [shouldAnimate, setShouldAnimate] = useState(false);

    // Mount for exit animation
    useEffect(() => {
        if (open) {
            setShow(true);
        } else {
            const timeout = setTimeout(() => setShow(false), 300);
            return () => clearTimeout(timeout);
        }
    }, [open]);

    // Animation-Trigger after mount
    useEffect(() => {
        if (show && open) {
            setShouldAnimate(false);
            const timeout = setTimeout(() => setShouldAnimate(true), 10);
            return () => clearTimeout(timeout);
        }
    }, [show, open]);

    // Close on ESC
    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [open, onClose]);

    if (!show) return null;

    return (
        <div className="fixed inset-0 z-mobileMenuOverlay flex h-dvh">
            {/* Overlay with fade animation */}
            <div
                className={cn(
                    "bg-black/40 flex-1 transition-opacity duration-300",
                    open ? "opacity-100" : "opacity-0 pointer-events-none"
                )}
                onClick={onClose}
            />
            {/* Drawer with slide-in/out animation */}
            <nav
                ref={drawerRef}
                className={cn(
                    "bg-background w-64 h-full shadow-lg p-4 flex flex-col transition-transform duration-300 ease-in-out focus:outline-none",
                    shouldAnimate && open ? "translate-x-0" : "translate-x-full",
                    "fixed right-0 top-0 bottom-0 z-mobileMenu"
                )}
                tabIndex={-1}
                aria-modal="true"
                role="dialog"
            >
                {/* User Profile */}
                <div className="flex items-center gap-3 mb-6">
                    <InitialsAvatar name={userData?.name || ""} size="md" />
                    <div className="flex flex-col">
                        <span className="font-semibold text-base text-ellipsis overflow-hidden whitespace-nowrap max-w-[120px]">
                            {userData?.name}
                        </span>
                        <span className="text-xs text-muted-foreground">{t("profile")}</span>
                    </div>
                </div>
                {/* Navigation */}
                <div className="flex flex-col gap-1 flex-1">
                    {mobileRoutes.map((route) => {
                        if (!checkPermissions(userData?.permissionCodes, route.permissions)) {
                            return null;
                        }

                        const isCurrent = pathname === route.path;
                        const Icon = route.icon;
                        return (
                            <Button
                                key={route.path}
                                variant={isCurrent ? "default" : "ghost"}
                                className={cn(
                                    "w-full flex items-center justify-start gap-2 px-2 py-2 rounded-lg",
                                    isCurrent
                                        ? "bg-foreground text-background hover:bg-foreground/90"
                                        : "text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                                )}
                                asChild
                                onClick={onClose}
                            >
                                <Link to={route.path} className="flex items-center gap-2 w-full">
                                    <Icon size={20} style={{ color: "inherit", flexShrink: 0 }} />
                                    <span className="text-md font-medium ml-2">{t(route.title)}</span>
                                    <TbChevronRight className="ml-auto" />
                                </Link>
                            </Button>
                        );
                    })}
                </div>
                {/* Settings & Theme */}
                <div className="mt-6 flex flex-col gap-2">
                    <Button variant="ghost" className="w-full flex items-center gap-2" asChild onClick={onClose}>
                        <Link to="/settings" className="flex items-center gap-2 w-full">
                            <TbSettings size={20} />
                            <span>{t("settings")}</span>
                        </Link>
                    </Button>
                    <Button variant="ghost" className="w-full flex items-center gap-2" onClick={toggleTheme}>
                        {theme === "dark" ? <TbSun size={20} /> : <TbMoon size={20} />}
                        <span>{theme === "dark" ? t("light_mode") : t("dark_mode")}</span>
                    </Button>
                    <Button
                        variant="ghost"
                        className="w-full flex items-center gap-2 text-red-500 hover:bg-red-500/20 hover:text-red-600"
                        onClick={() => {
                            handleLogout();
                            onClose();
                        }}
                    >
                        <TbLogout size={20} />
                        <span>{t("logout")}</span>
                    </Button>
                </div>
            </nav>
        </div>
    );
}
